import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import Header from "../components/Header";
import Ticker from "../components/Ticker";
import Footer from "../components/Footer";
import Sidebar from "../components/Sidebar";
import { ArticlesAPI, normalizeArticle } from "../lib/api";
import { getCategory } from "../mock";
import { NewsRiverRow } from "../components/ArticleCards";
import { AudioPlayerModal } from "../components/AudioPlayerModal";
import { Loader2 } from "lucide-react";

const PAGE_SIZE = 20;

const CategoryPage = () => {
  const { cat } = useParams();
  const c = getCategory(cat);
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [playingArticle, setPlayingArticle] = useState(null);

  useEffect(() => {
    setLoading(true);
    setVisible(PAGE_SIZE);
    ArticlesAPI.list({ category: cat, limit: 200 })
      .then((d) => {
        const sorted = d
          .map(normalizeArticle)
          .sort((a, b) => new Date(b.publishedAt || 0) - new Date(a.publishedAt || 0));
        setList(sorted);
      })
      .catch(() => setList([]))
      .finally(() => setLoading(false));
  }, [cat]);

  const shown = list.slice(0, visible);
  const hasMore = list.length > visible;

  return (
    <div>
      <Ticker />
      <Header />
      <main className="max-w-[1400px] mx-auto px-6 py-10">
        <div className="mb-8" data-testid="section-header">
          <div className="text-[10px] tracking-[0.35em] uppercase text-[#0891B2] font-condensed font-bold">Coverage</div>
          <h1 className="mt-2 font-condensed uppercase text-[42px] md:text-[56px] font-bold text-[#0B1F33] leading-none tracking-tight">{c.label}</h1>
          <div className="mt-3 h-[4px] w-[72px] bg-[#B8E9F1]" data-testid="section-stripe" />
          <p className="mt-4 text-[14px] text-neutral-600 max-w-2xl">Research, commentary and reporting from the {c.label} desk.</p>
        </div>

        {loading ? (
          <div className="py-24 flex items-center justify-center text-neutral-500">
            <Loader2 className="w-6 h-6 animate-spin mr-2 text-[#0891B2]" /> Loading…
          </div>
        ) : list.length === 0 ? (
          <div className="py-24 text-center">
            <p className="text-neutral-600">No pieces published in this section yet.</p>
            <Link to="/" className="inline-block mt-4 gf-link text-[#0891B2] uppercase tracking-widest text-sm">Back to home</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-8">
              <div data-testid="category-river">
                {shown.map((a) => (
                  <NewsRiverRow key={a.id} a={a} onListen={setPlayingArticle} />
                ))}
              </div>

              {hasMore && (
                <div className="mt-8 flex justify-center">
                  <button
                    onClick={() => setVisible((v) => v + PAGE_SIZE)}
                    className="px-8 py-3 bg-[#0B1F33] hover:bg-[#132f57] text-[#FFFFFF] rounded-none uppercase tracking-[0.2em] text-[11px] font-semibold transition-colors"
                  >
                    Load More ({list.length - visible} remaining)
                  </button>
                </div>
              )}
            </div>
            <aside className="lg:col-span-4">
              <Sidebar />
            </aside>
          </div>
        )}
      </main>
      <AudioPlayerModal
        open={Boolean(playingArticle)}
        article={playingArticle}
        onClose={() => setPlayingArticle(null)}
      />
      <Footer />
    </div>
  );
};

export default CategoryPage;
